import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { ApplicationListItem, InboundMessage, MailClassification } from '../../types';
import { formatDateTime } from '../../utils/format';
import { CLASSIFICATION_COLORS, CLASSIFICATION_LABELS, USER_CLASSIFICATIONS, senderLabel } from '../../utils/mail';

interface Props {
  messages: InboundMessage[];
  /** For "which application is this about?"; omit to hide that control (e.g. on an application's own page). */
  applications?: ApplicationListItem[];
  /** Called with the change; resolves when it is saved. */
  onChange: (message: InboundMessage, change: { application?: string | null; classification?: MailClassification }) => Promise<void>;
  empty: string;
}

/** Employer emails, newest first, each with what JobFlo made of it and the controls to correct that. */
const MessageList: React.FC<Props> = ({ messages, applications, onChange, empty }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const change = async (message: InboundMessage, data: { application?: string | null; classification?: MailClassification }) => {
    setBusy(message.id);
    try {
      await onChange(message, data);
    } finally {
      setBusy(null);
    }
  };

  if (messages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.5 }}>
      {messages.map((message) => {
        const locked = message.classification === 'verification';
        return (
          <Box component="li" key={message.id} sx={{ pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Chip size="small" color={CLASSIFICATION_COLORS[message.classification] ?? 'default'} label={CLASSIFICATION_LABELS[message.classification] ?? message.classification} />
              <Typography variant="caption" color="text.secondary">
                {formatDateTime(message.received_at)}
                {message.direction === 'copy' ? ' · copy from your mailbox' : ' · sent to your JobFlo address'}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {message.subject || 'No subject'}
            </Typography>
            {!locked && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                {senderLabel(message)}
              </Typography>
            )}
            {message.excerpt && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                {message.excerpt}
              </Typography>
            )}
            {message.application && applications && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                About{' '}
                <Link component={RouterLink} to={`/applications/${message.application.id}`}>
                  {message.application.job_title} at {message.application.company}
                </Link>
                {message.status_applied ? ' · moved its status' : ''}
              </Typography>
            )}
            {message.forward_error && message.direction === 'direct' && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                Not forwarded to your inbox: {message.forward_error}
              </Typography>
            )}
            {!locked && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                {applications && (
                  <TextField
                    select
                    size="small"
                    label="About which application"
                    value={message.application?.id ?? ''}
                    disabled={busy === message.id}
                    onChange={(e) => change(message, { application: e.target.value || null })}
                    sx={{ minWidth: 260, flex: 1 }}
                  >
                    <MenuItem value="">None of them</MenuItem>
                    {applications.map((application) => (
                      <MenuItem key={application.id} value={application.id}>
                        {application.job.title} at {application.job.company.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  select
                  size="small"
                  label="What it says"
                  value={message.classification}
                  disabled={busy === message.id}
                  onChange={(e) => change(message, { classification: e.target.value as MailClassification })}
                  sx={{ minWidth: 200 }}
                >
                  {USER_CLASSIFICATIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {CLASSIFICATION_LABELS[value]}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default MessageList;
